// Voice editor — shared section infrastructure.
//
// `SectionShell` is the common header + body wrapper every voice section
// (and the LeadTemp / Relances slots in `VoiceEditorPage`) renders into. An
// optional id provides a late-mount-safe anchor: matching hashes scroll the
// section into view and focus its heading for assistive-technology context.
// `SectionPropsWithVoice` is the shared prop shape for the voice-backed
// sections that mutate via the parent's `flush` handle. Extracted verbatim
// from `VoiceEditorPage.tsx` — no behavior change.

import { useEffect, useRef } from 'react';

import { type VoicePatch, type VoiceRow } from '@barel/api-client/react';

interface SectionShellProps {
  id?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

export function SectionShell({ id, title, subtitle, children }: SectionShellProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = id ? `${id}-title` : undefined;

  useEffect(() => {
    if (!id || window.location.hash !== `#${id}`) return;
    sectionRef.current?.scrollIntoView({ block: 'start' });
    headingRef.current?.focus({ preventScroll: true });
  }, [id]);

  return (
    <section
      ref={sectionRef}
      id={id}
      aria-labelledby={headingId}
      className="scroll-mt-6 flex flex-col gap-3"
    >
      <header>
        <h2
          ref={headingRef}
          id={headingId}
          tabIndex={id ? -1 : undefined}
          className="text-base font-semibold text-primary focus:outline-none"
        >
          {title}
        </h2>
        {subtitle ? <p className="mt-0.5 text-xs text-tertiary">{subtitle}</p> : null}
      </header>
      <div>{children}</div>
    </section>
  );
}

export interface SectionPropsWithVoice {
  voice: VoiceRow;
  flush: (patch: VoicePatch) => Promise<void>;
}
